import { DTConsumer_Private } from '@rabbit/data/types';

/**
 * Validates if a consumer has a complete address
 * @param consumerData - The consumer's private data
 * @returns boolean - True if address is complete, false otherwise
 */
export function hasValidAddress(consumerData: DTConsumer_Private | null | undefined): boolean {
  if (!consumerData?.address || !Array.isArray(consumerData.address) || consumerData.address.length === 0) {
    return false;
  }

  // Check if at least one address has the required fields
  const hasCompleteAddress = consumerData.address.some(address => {
    return (
      address.line1 &&
      address.line1.trim() !== '' &&
      address.town &&
      address.town.trim() !== '' &&
      address.postcode &&
      address.postcode.trim() !== '' &&
      address.country &&
      address.country.trim() !== ''
    );
  });

  return hasCompleteAddress;
}

/**
 * Validates if a consumer has a phone number populated
 * (We keep this intentionally simple: if the tenant needs stricter formatting,
 * it should be handled in the phone input component / profile editor.)
 */
export function hasValidPhoneNumber(
  consumerData: DTConsumer_Private | null | undefined
): boolean {
  const phone = consumerData?.phone;
  return typeof phone === 'string' && phone.trim().length > 0;
}

/**
 * Gets the primary/default address from consumer data
 * @param consumerData - The consumer's private data
 * @returns Address object or null if no valid address found
 */
export function getPrimaryAddress(consumerData: DTConsumer_Private | null | undefined) {
  if (!consumerData?.address || !Array.isArray(consumerData.address) || consumerData.address.length === 0) {
    return null;
  }

  // Look for default address first
  const defaultAddress = consumerData.address.find(address => address.isDefault);
  if (defaultAddress && hasValidAddressFields(defaultAddress)) {
    return defaultAddress;
  }

  // If no default, return first valid address
  const validAddress = consumerData.address.find(address => hasValidAddressFields(address));
  return validAddress || null;
}

/**
 * Helper function to check if individual address has required fields
 */
function hasValidAddressFields(address: any): boolean {
  return (
    address.line1 &&
    address.line1.trim() !== '' &&
    address.town &&
    address.town.trim() !== '' &&
    address.postcode &&
    address.postcode.trim() !== '' &&
    address.country &&
    address.country.trim() !== ''
  );
}
